import { useEffect, useState } from "react";
import { NavLink as Link, useParams } from "react-router-dom";
import { useAppSelector } from "../../store/hooks";
import Animation from "../Animation";
import Spinner from "../Spinner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { convertText } from "@/hooks/utils";
import { CheckCircle, Lock, Play } from "lucide-react";

export enum lessonsTypeEnum {
  RECORDED = "recorded",
}

const formatDate = (dateString: string) => {
  // Create a Date object from the string
  const date = new Date(dateString.replace(" ", "T")); // Replace space with 'T' for ISO format

  const formattedDate = date.toLocaleString("ar-EG", {
    year: "numeric",
    month: "numeric",
    day: "numeric",
  });

  return formattedDate; // Output: "July 29, 2023, 19:36:53"
};

const LessonsList = () => {
  // const { schoolgrade, group } = useParams();
  const params = useParams();
  const parts = params["*"]?.split("/");
  const group = parts?.[parts?.length - 1] ?? "";

  const [isMobile, setIsMobile] = useState(false);
  const { portal, userProfileData } = useAppSelector((state) => state.store);

  const lessons = portal?.data.filter(
    (item) => decodeURIComponent(item.slug) === group && !item.is_live_session
  );
  const catName = portal?.cats?.filter(
    (item) => decodeURIComponent(item.slug) === decodeURIComponent(group)
  )?.[0]?.name;

  const checkIsPurchased = (lectureId: string) => {
    return userProfileData?.purchases?.find(
      (purchase) => Number(purchase) === Number(lectureId)
    );
  };

  useEffect(() => {
    const userAgent =
      navigator.userAgent || navigator.vendor || (window as any).opera;
    setIsMobile(
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        userAgent
      )
    );
  }, []);

  return (
    <Animation>
      <div className="min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {!portal && <Spinner />}

          {portal && (
            <>
              <div className="mb-8">
                <nav className="flex mb-4 text-sm text-gray-600">
                  <Link
                    to={parts?.slice(0, -2).join("/") || "/"}
                    className="hover:text-blue-600 capitalize"
                  >
                    {catName?.indexOf("|") > -1
                      ? catName?.split("|")[0]
                      : catName}
                  </Link>
                  <span className="mx-2">/</span>
                  <span className="capitalize">{group?.replace("-", " ")}</span>
                </nav>

                <h1 className="text-3xl font-bold text-gray-900 mb-2 capitalize">
                  {catName?.indexOf("|") > -1 ? catName?.split("|")[0] : catName}
                </h1>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {lessons?.length ? (
                  [...lessons].reverse().map((lecture) => {
                    const isPurchased = checkIsPurchased(lecture.ID);
                    return (
                      <Card
                        key={lecture.ID}
                        className="hover:shadow-lg transition-shadow duration-200"
                      >
                        <CardHeader>
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <CardTitle className="text-xl mb-2 flex gap-2 items-center">
                                {isPurchased ? (
                                  true ? (
                                    <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                                  ) : (
                                    <Play className="w-5 h-5 text-blue-600 mr-2" />
                                  )
                                ) : (
                                  <Lock className="w-5 h-5 text-gray-400 mr-2" />
                                )}
                                {convertText(lecture.post_title)}
                              </CardTitle>
                              <p className="text-gray-600 text-sm">
                                تاريخ النشر : {formatDate(lecture.post_date)}
                              </p>
                            </div>

                            <div className="ml-4">
                              {isPurchased ? (
                                <Badge
                                  variant="default"
                                  className="bg-green-100 text-green-800"
                                >
                                  مفتوح
                                </Badge>
                              ) : (
                                <Badge
                                  variant="secondary"
                                  className="bg-red-100 text-red-800"
                                >
                                  مغلق
                                </Badge>
                              )}
                            </div>
                          </div>
                        </CardHeader>

                        <CardContent>
                          <div className="space-y-4">
                            {/* <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                            <div className="flex items-center">
                              <Clock className="w-4 h-4 mr-1" />
                              {lecture.duration}
                            </div>
                            <div className="flex items-center">
                              <Play className="w-4 h-4 mr-1" />
                              {lecture.videos} فيديو
                            </div>
                            <div className="flex items-center">
                              <FileText className="w-4 h-4 mr-1" />
                              {lecture.quizzes} اختبارات
                            </div>
                          </div> */}

                            <div className="flex space-x-2">
                              {isPurchased ? (
                                <Link
                                  to={`/${parts.join("/")}/${
                                    lecture.post_name
                                  }`}
                                  className="w-full"
                                >
                                  <Button
                                    className="flex-1 w-full hover:cursor-pointer"
                                    type="button"
                                  >
                                    شاهد الحصة
                                  </Button>
                                </Link>
                              ) : (
                                <Link
                                  to={`/${parts.join("/")}/${
                                    lecture.post_name
                                  }`}
                                  className="w-full"
                                >
                                  <Button
                                    type="button"
                                    className="flex-1 bg-gray-100 hover:cursor-pointer w-full"
                                    variant="outline"
                                  >
                                    {lecture.hide_lesson_price
                                      ? `اشتري الآن`
                                      : `اشتري الآن - ${lecture._regular_price} جنيه`}
                                  </Button>
                                </Link>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })
                ) : (
                  <p className="text-center text-gray-500 col-span-2">
                    لا توجد حصص مسجلة متاحة حالياً.
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </Animation>
  );
};

export default LessonsList;

// import { useEffect, useState } from "react";
// import { useParams } from "react-router-dom";
// import { useAppSelector } from "../../store/hooks";
// import Animation from "../Animation";
// import LessonsMap from "../lesson/LessonsMap";
// import Spinner from "../Spinner";

// export enum lessonsTypeEnum {
//   LIVE = "live",
//   RECORDED = "recorded",
// }

// const LessonsList = () => {
//   const { group } = useParams();

//   const [isMobile, setIsMobile] = useState(false);
//   const [lessonsType, setLessonsType] = useState(lessonsTypeEnum.LIVE);
//   const { portal } = useAppSelector((state) => state.store);
//   const lessons = portal?.data?.filter((item) => item.slug === group);

//   const catName = portal?.cats?.filter(
//     (item) => decodeURIComponent(item.slug) === decodeURIComponent(group)
//   )?.[0]?.name;

//   useEffect(() => {
//     //@ts-ignore
//     const userAgent = navigator.userAgent || navigator.vendor || window.opera;
//     setIsMobile(
//       /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
//         userAgent
//       )
//     );
//   }, []);

//   return (
//     <Animation>
//       <div className="teacher bg-ea-base h-100" style={{ minHeight: "90vh" }}>
//         <div
//           className="bg-primary"
//           style={{
//             padding: "1rem 0 7rem 0",
//             textAlign: "center",
//             width: "100%",
//           }}
//         ></div>
//         {portal ? (
//           <Animation>
//             <div className="row w-75 m-auto">
//               <div
//                 className="col-lg-4"
//                 style={{
//                   transform: "translateY(-70px)",
//                 }}
//               >
//                 <div className="card m-auto rounded-ea shadow">
//                   <div className="teacher-card">
//                     <img
//                       style={{ backgroundColor: "white" }}
//                       src={decodeURIComponent(portal.cardImg)}
//                       className="card-img-top rounded-ea"
//                       alt={portal.name}
//                     />
//                     <p
//                       className="teacher-name"
//                       style={{
//                         bottom: isMobile ? ".9rem" : "1.2rem",
//                       }}
//                     >
//                       {catName?.indexOf("|") > -1
//                         ? catName?.split("|")[0]
//                         : catName}
//                     </p>
//                   </div>
//                   <ul className="list-group list-group-flush border-0 rounded-ea">
//                     <li className="list-group-item">
//                       الحصص ({lessons?.length})
//                     </li>
//                     <li className="list-group-item">
//                       الإمتحانات ({lessons?.length})
//                     </li>
//                   </ul>
//                 </div>
//               </div>
//               <div className="col-lg-8 bg-white shadow rounded-ea my-3 pt-2">
//                 <div className="d-flex gap-2">
//                   <button
//                     className={`lesson-list-btn ${
//                       lessonsType === lessonsTypeEnum.LIVE ? "active" : ""
//                     }`}
//                     onClick={() => setLessonsType(lessonsTypeEnum.LIVE)}
//                   >
//                     حصص لايف
//                   </button>
//                   <button
//                     className={`lesson-list-btn ${
//                       lessonsType === lessonsTypeEnum.RECORDED ? "active" : ""
//                     }`}
//                     onClick={() => setLessonsType(lessonsTypeEnum.RECORDED)}
//                   >
//                     حصص مسجلة
//                   </button>
//                 </div>
//                 {!lessons ? (
//                   <Spinner />
//                 ) : (
//                   <LessonsMap
//                     isLiveSession={lessonsType === lessonsTypeEnum.LIVE}
//                   />
//                 )}
//               </div>
//             </div>
//           </Animation>
//         ) : (
//           <Spinner />
//         )}
//       </div>
//     </Animation>
//   );
// };

// export default LessonsList;
